import type { CustomerProfileUpdateInput } from '../types'
import type { CustomerPrivacyDashboard } from '../customerPrivacyApi'
import { useShopI18n } from '../../i18n'
import {
  PrivacyPageContent,
  PrivacyPageStateShell,
} from './customer-privacy'
import { useCustomerPrivacyPageController } from './customer-privacy/useCustomerPrivacyPageController'

type CustomerPrivacyPageProps = {
  dashboard: CustomerPrivacyDashboard | null
  loading: boolean
  refreshing: boolean
  error: string
  notice: string
  exportRequesting: boolean
  exportDownloading: boolean
  consentsSaving: boolean
  deletionSubmitting: boolean
  onUpdateProfile: (payload: CustomerProfileUpdateInput) => Promise<boolean>
  onRefresh: () => void
  onRequestExport: () => void
  onDownloadExport: () => void
  onSaveConsents: (input: { marketingEmail: boolean; promotionalNotifications: boolean }) => void
  onRequestDeletion: (password: string) => void
  onNavigate: (targetPath: string) => void
}

export const CustomerPrivacyPage = ({
  dashboard,
  loading,
  refreshing,
  error,
  notice,
  exportRequesting,
  exportDownloading,
  consentsSaving,
  deletionSubmitting,
  onUpdateProfile,
  onRefresh,
  onRequestExport,
  onDownloadExport,
  onSaveConsents,
  onRequestDeletion,
  onNavigate,
}: CustomerPrivacyPageProps) => {
  const { language, t, locale, toLocalizedPath } = useShopI18n()
  const {
    navItems,
    latestRequest,
    accountStatusLabel,
    exportStateLabel,
    readyToDownload,
    privacyUpdatedAtValue,
  } = useCustomerPrivacyPageController({
    dashboard,
    language,
    locale,
    t,
    toLocalizedPath,
  })

  if (loading && !dashboard) {
    return (
      <PrivacyPageStateShell
        eyebrow={t('common.customerArea')}
        title={t('customer.privacy.title')}
        subtitle={t('customer.privacy.subtitle')}
        navItems={navItems}
        onNavigate={onNavigate}
      >
        <div className="ca-card-shell ca-theme-transition rounded-2xl px-4 py-3 text-sm text-(--color-text-secondary)">
          {t('customer.privacy.refreshing')}
        </div>
      </PrivacyPageStateShell>
    )
  }

  if (!dashboard) {
    return (
      <PrivacyPageStateShell
        eyebrow={t('common.customerArea')}
        title={t('customer.privacy.title')}
        subtitle={t('customer.privacy.subtitle')}
        navItems={navItems}
        onNavigate={onNavigate}
      >
        <div className="rounded-2xl border border-(--color-status-error-border) bg-(--color-status-error-bg) px-4 py-3 text-sm text-(--color-status-error)">
          {error || t('customer.privacy.unavailableDescription')}
        </div>
      </PrivacyPageStateShell>
    )
  }

  return (
    <PrivacyPageContent
      dashboard={dashboard}
      navItems={navItems}
      latestRequest={latestRequest}
      accountStatusLabel={accountStatusLabel}
      exportStateLabel={exportStateLabel}
      readyToDownload={readyToDownload}
      privacyUpdatedAtValue={privacyUpdatedAtValue}
      refreshing={refreshing}
      error={error}
      notice={notice}
      exportRequesting={exportRequesting}
      exportDownloading={exportDownloading}
      consentsSaving={consentsSaving}
      deletionSubmitting={deletionSubmitting}
      locale={locale}
      t={t}
      toLocalizedPath={toLocalizedPath}
      onUpdateProfile={onUpdateProfile}
      onRefresh={onRefresh}
      onRequestExport={onRequestExport}
      onDownloadExport={onDownloadExport}
      onSaveConsents={onSaveConsents}
      onRequestDeletion={onRequestDeletion}
      onNavigate={onNavigate}
    />
  )
}
